import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dropdown, Badge, Spinner, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaBell } from "react-icons/fa";

import { 
    getNotifications, 
    getNotificationCount, 
    markNotificationsRead,
    deleteNotification 
} from '@api/notifications';
import './NotificationCenter.scss';

const NotificationCenter = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const pollIntervalRef = useRef(null);

    // Fetch notification count
    const fetchCount = useCallback(async () => {
        try {
            const response = await getNotificationCount();
            setUnreadCount(response.data.unread_count || 0);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    }, []);

    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getNotifications();
            setNotifications(response.data || []);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch and polling
    useEffect(() => {
        fetchCount();
        
        // Poll for new notifications every 30 seconds
        pollIntervalRef.current = setInterval(() => {
            fetchCount();
        }, 30000);

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [fetchCount]);

    // Fetch notifications when dropdown opens
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen, fetchNotifications]);

    // Handle notification click
    const handleNotificationClick = async (notification) => {
        // Mark as read
        if (!notification.is_read) {
            try {
                await markNotificationsRead([notification.id]);
                setNotifications(prev => 
                    prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        }

        // Navigate if action_url exists
        if (notification.action_url) {
            setIsOpen(false);
            navigate(notification.action_url);
        }
    };

    // Handle delete notification
    const handleDelete = async (e, notificationId) => {
        e.stopPropagation();
        try {
            await deleteNotification(notificationId);
            const deletedNotification = notifications.find(n => n.id === notificationId);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            if (deletedNotification && !deletedNotification.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    // Mark all as read
    const handleMarkAllRead = async () => {
        try {
            await markNotificationsRead([], true);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // Get color class for notification type
    const getNotificationColor = (type, priority) => {
        if (priority === 'high' || priority === 'urgent') {
            return 'notification-urgent';
        }
        const colors = {
            'connection_request': 'notification-primary',
            'connection_accepted': 'notification-success',
            'new_follower': 'notification-info',
            'enrollment_request': 'notification-warning',
            'enrollment_approved': 'notification-success',
            'enrollment_rejected': 'notification-danger',
            'new_note': 'notification-primary',
            'note_reply': 'notification-secondary',
            'new_message': 'notification-info',
            'meeting_invite': 'notification-primary',
            'meeting_cancelled': 'notification-danger',
        };
        return colors[type] || 'notification-secondary';
    };

    return (
        <Dropdown 
            className="notification-center" 
            show={isOpen} 
            onToggle={(nextShow) => setIsOpen(nextShow)}
            align="end"
        >
            <Dropdown.Toggle 
                as="button" 
                className="notification-toggle"
                id="notification-dropdown"
            >
                <FaBell />
                {unreadCount > 0 && (
                    <Badge 
                        pill 
                        className="notification-badge"
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                )}
            </Dropdown.Toggle>

            <Dropdown.Menu className="notification-menu">
                <div className="notification-header">
                    <h6 className="mb-0">Notifications</h6>
                    {unreadCount > 0 && (
                        <Button 
                            variant="link" 
                            size="sm" 
                            className="mark-all-read"
                            onClick={handleMarkAllRead}
                        >
                            Mark all as read
                        </Button>
                    )}
                </div>

                <div className="notification-list">
                    {loading ? (
                        <div className="notification-loading">
                            <Spinner animation="border" size="sm" />
                            <span>Loading...</span>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="notification-empty">
                            <i className="bi bi-bell-slash"></i>
                            <p>No notifications yet</p>
                        </div>
                    ) : (
                        notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`notification-item ${!notification.is_read ? 'unread' : ''} ${getNotificationColor(notification.notification_type, notification.priority)}`}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div className="notification-content">
                                    <div className="notification-title">
                                        <div className="notification-icon">
                                            <FaBell />
                                        </div>
                                        {notification.title}
                                        {!notification.is_read && (
                                            <span className="unread-dot"></span>
                                        )}
                                    </div>
                                    <div className="notification-message">
                                        {notification.message}
                                    </div>
                                    <div className="notification-time">
                                        {notification.time_ago}
                                    </div>
                                </div>
                                <button 
                                    className="notification-delete"
                                    onClick={(e) => handleDelete(e, notification.id)}
                                    title="Delete notification"
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {notifications.length > 0 && (
                    <div className="notification-footer">
                        <Button 
                            variant="link" 
                            size="sm"
                            onClick={() => {
                                setIsOpen(false);
                                navigate('/notifications');
                            }}
                        >
                            View all notifications
                        </Button>
                    </div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default NotificationCenter;




